import { useEffect, useRef, useState } from "react";
import { api, errText, mediaUrl, getToken, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, CreatorName, formatNum, Loading, Empty, Skeleton, VScore } from "../components/common";
import { StoryShareModal } from "../components/StoryShareModal";
import { PostCard } from "../components/PostCard";
import { StoryBar, StoryViewer } from "../components/Stories";
import { TipModal } from "../components/TipModal";
import { GifPicker } from "../components/GifPicker";
import { FramedAvatar, BadgePill, bgStyle } from "../components/storeui";
import { ProfileDashboard } from "../components/ProfileDashboard";
import VirtuCity from "./VirtuCity";
import { CitizenCard } from "../components/CitizenCard";
import { CrewScreen, PhoneSettings, PeopleSheet, parsePhoneHash, exchangePhoneCode } from "../components/PhoneScreens";
import { PhoneThread, UserProfileSheet, profileFromClick } from "../components/PhoneSocial";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";
import {
  Home, Compass, Music2, Tv, Calendar, User, Heart, UserPlus, Check, Play, Pause,
  Search, LogIn, X, Link2, Copy, MoreHorizontal, ExternalLink, Smartphone, MessageSquare, Send, ArrowLeft,
  Plus, Film, Grid3x3, Instagram, ImagePlus, Loader2, PlusCircle, Bell, Newspaper, RefreshCw, Repeat2, Coins, Radio, Trophy, MessageCircle, Volume2, VolumeX, LayoutDashboard, SkipBack, SkipForward, ListMusic, ChevronDown, ShieldCheck, Building2, Shield, Settings
} from "lucide-react";

const TABS = [["feed", Home, "Home"], ["discover", Compass, "Explore"], ["create", Plus, "Create"], ["reels", Film, "Reels"], ["profile", User, "Profile"]];

// Session persistence for the in-world (Second Life) browser, which often wipes localStorage between
// sessions. We (a) accept a token supplied by the HUD via ?t= / #t=, and (b) mirror it into a
// 30-day cookie so the device stays signed in even when localStorage is cleared.
const SL_COOKIE = "vs_sl_token";
function readSlCookie() { const m = document.cookie.match(/(?:^|; )vs_sl_token=([^;]*)/); return m ? decodeURIComponent(m[1]) : null; }
function writeSlCookie(v) { try { document.cookie = `${SL_COOKIE}=${encodeURIComponent(v)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`; } catch { /* ignore */ } }
function tokenFromUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("t")) return sp.get("t");
    const h = window.location.hash || "";
    const m = h.match(/(?:token|t)=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

const IS_CITY = typeof window !== "undefined" && window.location.pathname.startsWith("/city");

export default function SLPhone() {
  const { user, login, refresh, logout } = useAuth();
  const booted = useRef(false);

  // Restore a session from the phone's single-use code, a legacy URL token, or the persistent cookie.
  const [hashInfo] = useState(() => (IS_CITY ? parsePhoneHash() : {}));
  useEffect(() => {
    if (booted.current) return; booted.current = true;
    const clearUrl = () => { try { const u = new URL(window.location.href); u.searchParams.delete("t"); u.hash = ""; window.history.replaceState({}, "", u.pathname + u.search); } catch { /* ignore */ } };
    if (hashInfo.code) {
      clearUrl();
      exchangePhoneCode(hashInfo.code).then(() => refresh()).catch((e) => { toast.error(errText(e)); const c = readSlCookie(); if (c && c !== getToken()) { setToken(c); refresh(); } });
      return;
    }
    const urlTok = hashInfo.token || tokenFromUrl();
    const existing = getToken();
    const tok = IS_CITY ? (urlTok || existing || readSlCookie()) : (existing || urlTok || readSlCookie());
    if (tok && tok !== existing) { setToken(tok); refresh(); }
    if (urlTok) clearUrl();
  }, []); // eslint-disable-line

  // Keep the cookie in sync so the device survives localStorage being wiped by the SL browser.
  useEffect(() => { if (user) { const t = getToken(); if (t) writeSlCookie(t); } }, [user]);

  const [tab, setTab] = useState(hashInfo.screen || (IS_CITY ? "city" : "feed"));
  const [signin, setSignin] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [openConv, setOpenConv] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState("post");
  const [searchQ, setSearchQ] = useState("");
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const audioRef = useRef(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [queue, setQueue] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [curTime, setCurTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!user) { setUnread(0); setMsgUnread(0); return; }
    const load = () => {
      api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
      api.get("/messages/unread-count").then(({ data }) => setMsgUnread(data.count)).catch(() => {});
    };
    load(); const i = setInterval(load, 20000); return () => clearInterval(i);
  }, [user, tab]);

  const start = (t) => {
    const a = audioRef.current;
    setNowPlaying(t); a.src = mediaUrl(t.audio_url); a.play().catch(() => {});
  };
  const playTrack = (t, list) => {
    const a = audioRef.current;
    if (list && list.length) { setQueue(list); setQIndex(Math.max(0, list.findIndex((x) => x.id === t.id))); }
    else { setQueue([t]); setQIndex(0); }
    if (nowPlaying?.id === t.id) { a.paused ? a.play() : a.pause(); return; }
    start(t);
  };
  const playAt = (i) => { if (i < 0 || i >= queue.length) return; setQIndex(i); start(queue[i]); };
  const next = () => playAt(qIndex + 1);
  const prev = () => { const a = audioRef.current; if (a.currentTime > 3) { a.currentTime = 0; return; } playAt(qIndex - 1); };
  const togglePlay = () => { const a = audioRef.current; a.paused ? a.play() : a.pause(); };
  const seek = (pct) => { const a = audioRef.current; if (a.duration) a.currentTime = pct * a.duration; };

  const openCreate = (mode = "post") => { if (!user) return setSignin(true); setCreateMode(mode); setCreateOpen(true); };
  const submitSearch = (e) => { e.preventDefault(); setSearchQ(q); setTab("discover"); };

  return (
    <div className="fixed inset-0 bg-[#050a18] text-white flex flex-col mx-auto max-w-[512px] overflow-hidden" style={{ fontSize: 15, backgroundImage: "radial-gradient(120% 60% at 50% -10%, rgba(37,99,235,.16), transparent 60%)" }} data-testid="sl-phone-root">
      {/* Header — mirrors the main mobile top header (logo · search · messages · notifications · account) */}
      <header className="flex items-center gap-1.5 px-2.5 h-14 border-b border-white/10 glass shrink-0">
        <img src="/virtustars-logo.png" alt="" className="h-7 w-auto shrink-0" />
        <form onSubmit={submitSearch} className="flex-1 min-w-0 flex items-center gap-2 surface-2 rounded-full px-3 h-9">
          <Search size={15} className="text-muted-foreground shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="bg-transparent flex-1 min-w-0 text-sm outline-none" data-testid="sl-search-input" aria-label="Search" />
        </form>
        {IS_CITY && <button onClick={() => (user ? setTab("city") : setSignin(true))} aria-label="VirtuCity" data-testid="sl-header-city"
          className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-full ${tab === "city" ? "text-gold bg-gold/10" : "text-white hover:bg-white/5"}`}>
          <Building2 size={20} />
        </button>}
        <button onClick={() => (user ? setTab("messages") : setSignin(true))} aria-label="Messages" data-testid="sl-header-messages"
          className={`relative shrink-0 w-9 h-9 flex items-center justify-center rounded-full ${tab === "messages" ? "text-eblue bg-eblue/10" : "text-white hover:bg-white/5"}`}>
          <MessageSquare size={20} />
          {msgUnread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{msgUnread > 9 ? "9+" : msgUnread}</span>}
        </button>
        <button onClick={() => (user ? setTab("notifications") : setSignin(true))} aria-label="Notifications" data-testid="sl-header-notifications"
          className={`relative shrink-0 w-9 h-9 flex items-center justify-center rounded-full ${tab === "notifications" ? "text-eblue bg-eblue/10" : "text-white hover:bg-white/5"}`}>
          <Bell size={20} />
          {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-eblue" />}
        </button>
        <button onClick={() => setSignin(true)} className="shrink-0 flex items-center" data-testid="sl-account-btn">
          {user ? <Avatar src={user.avatar_url} name={user.display_name} size={30} /> : <span className="btn-gold rounded-full px-3 py-1.5 flex items-center gap-1 text-xs"><LogIn size={13} /> Sign in</span>}
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto hide-scroll" data-testid="sl-content"
        onClickCapture={(e) => { const n = profileFromClick(e); if (n) { e.preventDefault(); e.stopPropagation(); setViewUser(n); } }}>
        {tab === "city" && (user ? <VirtuCity /> : <Empty icon={Building2} title="Sign in to VirtuStars" hint="Open your phone from inside VirtuCity to sign in automatically." />)}
        {tab === "feed" && <Feed user={user} onSignin={() => setSignin(true)} onAddStory={() => openCreate("story")} />}
        {tab === "discover" && <Discover initialQ={searchQ} onPlay={playTrack} nowPlaying={nowPlaying} playing={playing} user={user} onSignin={() => setSignin(true)} onTab={setTab} />}
        {tab === "reels" && <ReelsTab user={user} onSignin={() => setSignin(true)} />}
        {tab === "profile" && <ProfileTab user={user} onSignin={() => setSignin(true)} onTab={setTab} />}
        {tab === "music" && <MusicTab onPlay={playTrack} nowPlaying={nowPlaying} playing={playing} />}
        {tab === "tv" && <TVTab />}
        {tab === "messages" && <MessagesTab user={user} onSignin={() => setSignin(true)} initialConv={openConv} onConvUsed={() => setOpenConv(null)} />}
        {viewUser && <UserProfileSheet username={viewUser} me={user} onClose={() => setViewUser(null)} onSignin={() => setSignin(true)}
          onMessage={(c) => { setViewUser(null); setOpenConv(c); setTab("messages"); }} />}
        {tab === "notifications" && <NotificationsTab user={user} onSignin={() => setSignin(true)} onRead={() => setUnread(0)} />}
        {tab === "events" && <Events />}
        {tab === "career" && <Career user={user} onSignin={() => setSignin(true)} />}
        {tab === "crew" && <CrewScreen user={user} onSignin={() => setSignin(true)} />}
        {tab === "settings" && <PhoneSettings user={user} onSignin={() => setSignin(true)} onLogout={() => { logout(); try { document.cookie = "vs_sl_token=; path=/; max-age=0"; } catch { /* ignore */ } setTab("feed"); }} />}
      </main>

      {/* Now playing — tap to expand into the full-screen player */}
      {nowPlaying && !expanded && (
        <button onClick={() => setExpanded(true)} className="relative flex items-center gap-2 px-3 h-14 border-t border-white/10 glass shrink-0 text-left w-full" data-testid="sl-mini-player">
          <span className="absolute top-0 left-0 h-0.5 bg-eblue" style={{ width: `${duration ? (curTime / duration) * 100 : 0}%` }} />
          <img src={nowPlaying.cover_url} className="w-9 h-9 rounded object-cover" alt="" />
          <div className="min-w-0 flex-1"><div className="text-xs font-semibold truncate">{nowPlaying.title}</div><div className="text-[10px] text-muted-foreground truncate">{nowPlaying.artist?.display_name}</div></div>
          <span onClick={(e) => { e.stopPropagation(); prev(); }} className="w-8 h-8 flex items-center justify-center text-muted-foreground"><SkipBack size={16} /></span>
          <span onClick={(e) => { e.stopPropagation(); togglePlay(); }} data-testid="sl-mini-playpause" className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center">{playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}</span>
          <span onClick={(e) => { e.stopPropagation(); next(); }} className="w-8 h-8 flex items-center justify-center text-muted-foreground"><SkipForward size={16} /></span>
        </button>
      )}
      {nowPlaying && expanded && (
        <NowPlaying track={nowPlaying} playing={playing} curTime={curTime} duration={duration} queue={queue} qIndex={qIndex}
          onSeek={seek} onToggle={togglePlay} onNext={next} onPrev={prev} onPlayAt={playAt} onClose={() => setExpanded(false)} />
      )}
      <audio ref={audioRef}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onEnded={next} />

      {/* Bottom nav — matches the main mobile app (Home · Explore · Create · Reels · Profile) */}
      <nav className="grid grid-cols-5 border-t border-white/10 glass shrink-0 h-16 items-center px-2">
        {TABS.map(([k, Icon, label]) => k === "create" ? (
          <button key={k} onClick={() => openCreate("post")} data-testid="sl-nav-create" className="flex items-center justify-center">
            <span className="w-12 h-12 -mt-4 rounded-2xl btn-gold flex items-center justify-center shadow-lg"><Plus size={24} /></span>
          </button>
        ) : (
          <button key={k} onClick={() => setTab(k)} data-testid={`sl-nav-${k}`} className={`flex flex-col items-center justify-center gap-0.5 text-[10px] ${tab === k ? "text-eblue" : "text-muted-foreground"}`}>
            <Icon size={20} />{label}
          </button>
        ))}
      </nav>

      {signin && <SignIn user={user} onClose={() => setSignin(false)} login={login} refresh={refresh} />}
      {createOpen && <CreateSheet user={user} defaultMode={createMode} onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function Feed({ user, onSignin, onAddStory }) {
  const [posts, setPosts] = useState(null);
  const [stories, setStories] = useState([]);
  const [lives, setLives] = useState([]);
  const [storyOpen, setStoryOpen] = useState(-1);
  const [scope, setScope] = useState("all");
  const [skip, setSkip] = useState(0);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = (reset) => {
    const s = reset ? 0 : skip;
    return api.get(`/feed?skip=${s}&limit=10&scope=${scope}`).then(({ data }) => {
      setPosts((p) => (reset || !p ? data : [...p, ...data]));
      setMore(data.length === 10); setSkip(s + data.length); setLoading(false);
    });
  };
  const loadSide = () => {
    api.get("/stories").then(({ data }) => setStories(data)).catch(() => {});
    api.get("/live").then(({ data }) => setLives(data)).catch(() => {});
  };
  useEffect(() => { loadSide(); }, []); // eslint-disable-line
  useEffect(() => { setLoading(true); setSkip(0); setPosts(null); load(true); }, [scope]); // eslint-disable-line

  return (
    <div className="p-3" data-testid="sl-feed">
      <div className="mb-3 surface rounded-2xl p-3"><StoryBar groups={stories} lives={lives} onAdd={() => (user ? onAddStory() : onSignin())} onOpen={setStoryOpen} /></div>
      <div className="flex items-center gap-1 mb-3 surface rounded-full p-1 w-fit mx-auto" data-testid="sl-feed-scope-tabs">
        {[["all", "For You"], ["following", "Following"]].map(([k, label]) => (
          <button key={k} onClick={() => setScope(k)} data-testid={`sl-feed-scope-${k}`}
            className={`px-5 h-9 rounded-full text-sm font-semibold transition-colors ${scope === k ? "btn-blue text-white" : "text-muted-foreground hover:text-white"}`}>{label}</button>
        ))}
      </div>
      {loading || posts === null ? (
        <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="surface rounded-2xl p-4"><div className="flex gap-3 mb-3"><Skeleton className="w-11 h-11 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-32" /><Skeleton className="h-2 w-20" /></div></div><Skeleton className="h-48 w-full" /></div>)}</div>
      ) : posts.length === 0 ? (
        scope === "following"
          ? <Empty icon={Newspaper} title="No posts from people you follow yet" hint="Follow more creators, or switch to For You to discover the industry." />
          : <Empty icon={Newspaper} title="Your feed is quiet" hint="Follow creators and post something to bring your feed to life." />
      ) : (
        <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
      )}
      {more && !loading && posts?.length > 0 && <button onClick={() => load(false)} className="w-full surface-2 rounded-xl py-2.5 text-sm mt-4">Load more</button>}

      {storyOpen >= 0 && <StoryViewer groups={stories} startIndex={storyOpen} onClose={() => setStoryOpen(-1)} />}
    </div>
  );
}

function Discover({ initialQ = "", onPlay, nowPlaying, playing, user, onSignin, onTab }) {
  const [q, setQ] = useState(initialQ);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [shows, setShows] = useState([]);
  const [activeVid, setActiveVid] = useState(null);

  useEffect(() => { setQ(initialQ); }, [initialQ]);
  useEffect(() => {
    api.get("/suggested").then(({ data }) => setSuggested(data)).catch(() => setSuggested([]));
    api.get("/music/tracks?sort=trending").then(({ data }) => setTracks(data.slice(0, 8))).catch(() => {});
    api.get("/tv/videos").then(({ data }) => setShows(data.slice(0, 6))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!q) { setResults(null); return; }
    setSearching(true);
    api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => setResults(null)).finally(() => setSearching(false));
  }, [q]);

  const follow = async (u, i) => {
    if (!user) return onSignin();
    try { await api.post(`/follow/${u.id}`); setSuggested((s) => s.map((x, j) => j === i ? { ...x, _followed: true } : x)); toast.success(`Following ${u.display_name}`); }
    catch { toast.error("Could not follow"); }
  };

  const SHORTCUTS = [["music", Music2, "Music"], ["tv", Tv, "VStar TV"], ["events", Calendar, "Events"], ["reels", Film, "Reels"], ["career", User, "My Career"]];

  const CreatorRow = ({ u, i, canFollow }) => (
    <div className="surface rounded-xl p-2.5 flex items-center gap-3" data-testid={`sl-creator-${u.id}`}>
      <Avatar src={u.avatar_url} name={u.display_name} size={44} />
      <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate"><CreatorName user={u} /></div><div className="text-[11px] text-muted-foreground truncate">{u.roles?.primary || "Creator"} · {formatNum(u.follower_count)} followers</div></div>
      {canFollow && <button onClick={() => follow(u, i)} disabled={u._followed} data-testid={`sl-follow-${u.username}`} className={`shrink-0 rounded-full px-3 h-8 text-xs font-semibold flex items-center gap-1 ${u._followed ? "btn-gold text-black" : "btn-blue text-white"}`}>{u._followed ? <><Check size={13} /> Following</> : <><UserPlus size={13} /> Follow</>}</button>}
    </div>
  );

  const TrackRow = ({ t, list }) => { const active = nowPlaying?.id === t.id; return (
    <button onClick={() => onPlay(t, list)} className="w-full surface rounded-xl p-2.5 flex items-center gap-3 text-left" data-testid={`sl-track-${t.id}`}>
      <div className="relative shrink-0"><img src={t.cover_url} className="w-12 h-12 rounded object-cover" alt="" /><span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">{active && playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}</span></div>
      <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{t.title}</div><div className="text-[11px] text-muted-foreground truncate">{t.artist?.display_name} · {formatNum(t.stream_count)} streams</div></div>
    </button>
  ); };

  const ShowCard = ({ v }) => (
    <button onClick={() => setActiveVid(v)} className="surface rounded-xl overflow-hidden text-left" data-testid={`sl-show-${v.id}`}>
      <div className="relative aspect-video bg-black"><img src={v.thumbnail_url} className="w-full h-full object-cover" alt="" loading="lazy" /><span className="absolute inset-0 flex items-center justify-center"><span className="w-9 h-9 rounded-full btn-gold flex items-center justify-center"><Play size={16} /></span></span></div>
      <div className="p-2"><div className="text-xs font-medium line-clamp-1">{v.title}</div><div className="text-[10px] text-muted-foreground truncate">{v.channel?.display_name}</div></div>
    </button>
  );

  const Head = ({ title, sub }) => (<div className="mb-2"><div className="font-display font-bold text-base">{title}</div>{sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}</div>);

  return (
    <div className="p-3 space-y-6" data-testid="sl-explore">
      <div className="flex items-center gap-2 surface-2 rounded-full px-3 h-10"><Search size={15} className="text-muted-foreground" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators, music, shows…" className="bg-transparent flex-1 text-sm outline-none" data-testid="sl-explore-search" /></div>

      {!q && (
        <div className="flex gap-2 overflow-x-auto hide-scroll" data-testid="sl-explore-shortcuts">
          {SHORTCUTS.map(([k, Icon, label]) => <button key={k} onClick={() => onTab(k)} className="shrink-0 flex items-center gap-1.5 surface-2 rounded-full px-3.5 h-9 text-sm text-muted-foreground"><Icon size={15} />{label}</button>)}
        </div>
      )}

      {activeVid && <video src={mediaUrl(activeVid.video_url)} poster={activeVid.thumbnail_url} controls autoPlay className="w-full rounded-xl aspect-video bg-black" data-testid="sl-explore-player" />}

      {q ? (
        searching ? <Loading /> : results && (
          <div className="space-y-6">
            {results.users?.length > 0 && <div><Head title="Creators" /><div className="space-y-2">{results.users.map((u, i) => <CreatorRow key={u.id} u={u} i={i} canFollow />)}</div></div>}
            {results.tracks?.length > 0 && <div><Head title="Music" /><div className="space-y-2">{results.tracks.map((t) => <TrackRow key={t.id} t={t} list={results.tracks} />)}</div></div>}
            {results.videos?.length > 0 && <div><Head title="VStar TV" /><div className="grid grid-cols-2 gap-2">{results.videos.map((v) => <ShowCard key={v.id} v={v} />)}</div></div>}
            {!results.users?.length && !results.tracks?.length && !results.videos?.length && <Empty icon={Search} title="No results" hint={`Nothing found for "${q}"`} />}
          </div>
        )
      ) : (
        <div className="space-y-6">
          <div>
            <Head title="Creators to discover" sub="Fresh talent across the VirtuStars industry" />
            {suggested === null ? <Loading /> : suggested.length === 0 ? <Empty icon={Compass} title="No creators yet" /> : <div className="space-y-2">{suggested.map((u, i) => <CreatorRow key={u.id} u={u} i={i} canFollow />)}</div>}
          </div>
          {tracks.length > 0 && <div>
            <Head title="Trending on VStar Music" />
            <div className="space-y-2">{tracks.map((t) => <TrackRow key={t.id} t={t} list={tracks} />)}</div>
          </div>}
          {shows.length > 0 && <div>
            <Head title="On VStar TV" />
            <div className="grid grid-cols-2 gap-2">{shows.map((v) => <ShowCard key={v.id} v={v} />)}</div>
          </div>}
        </div>
      )}
    </div>
  );
}

function fmtTime(s) { if (!s || isNaN(s)) return "0:00"; const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, "0")}`; }

// Full-screen now-playing view with scrubber + queue (mirrors the main app player)
function NowPlaying({ track, playing, curTime, duration, queue, qIndex, onSeek, onToggle, onNext, onPrev, onPlayAt, onClose }) {
  const [showQueue, setShowQueue] = useState(false);
  const barRef = useRef();
  const pct = duration ? (curTime / duration) * 100 : 0;
  const upcoming = queue.slice(qIndex + 1);
  const handleSeek = (e) => { const r = barRef.current.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width; onSeek(Math.min(1, Math.max(0, x))); };

  return (
    <div className="absolute inset-0 z-50 flex flex-col overflow-hidden bg-[#07070c]" data-testid="sl-now-playing">
      <img src={track.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-2xl scale-125" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-[#07070c]/80 to-[#07070c]" />
      <div className="relative flex flex-col h-full p-5">
        <div className="flex items-center justify-between">
          <button onClick={onClose} data-testid="sl-np-close" className="w-9 h-9 rounded-full glass flex items-center justify-center"><ChevronDown size={20} /></button>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">VStar Music</div>
          <button onClick={() => setShowQueue((s) => !s)} data-testid="sl-np-queue-toggle" className={`w-9 h-9 rounded-full glass flex items-center justify-center ${showQueue ? "text-eblue" : ""}`}><ListMusic size={18} /></button>
        </div>

        {showQueue ? (
          <div className="flex-1 overflow-y-auto hide-scroll mt-5" data-testid="sl-np-queue">
            <div className="font-display font-bold text-base mb-3">Up Next</div>
            {upcoming.length === 0 ? <p className="text-sm text-muted-foreground">End of queue</p> : (
              <div className="space-y-2">{upcoming.map((t, i) => (
                <button key={`${t.id}-${i}`} onClick={() => onPlayAt(qIndex + 1 + i)} className="w-full flex items-center gap-3 surface rounded-xl p-2.5 text-left" data-testid={`sl-np-queue-item-${t.id}`}>
                  <img src={t.cover_url} className="w-11 h-11 rounded object-cover" alt="" />
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{t.title}</div><div className="text-[11px] text-muted-foreground truncate">{t.artist?.display_name}</div></div>
                  <Play size={15} className="text-muted-foreground" />
                </button>
              ))}</div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center">
            <img src={track.cover_url} alt="" className="w-56 h-56 max-w-[70%] rounded-2xl object-cover shadow-2xl" />
          </div>
        )}

        <div className="mt-4">
          <div className="font-display font-bold text-xl truncate">{track.title}</div>
          <div className="text-sm text-muted-foreground truncate">{track.artist?.display_name}{track.genre ? ` · ${track.genre}` : ""}</div>

          <div ref={barRef} onClick={handleSeek} className="mt-4 h-1.5 rounded-full bg-white/15 cursor-pointer relative" data-testid="sl-np-scrubber">
            <div className="h-full rounded-full bg-gradient-to-r from-eblue to-gold" style={{ width: `${pct}%` }} />
            <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow" style={{ left: `calc(${pct}% - 6px)` }} />
          </div>
          <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5 font-mono"><span>{fmtTime(curTime)}</span><span>{fmtTime(duration)}</span></div>

          <div className="flex items-center justify-center gap-8 mt-4 mb-2">
            <button onClick={onPrev} data-testid="sl-np-prev" className="text-white/90"><SkipBack size={28} /></button>
            <button onClick={onToggle} data-testid="sl-np-playpause" className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center shadow-xl">{playing ? <Pause size={28} /> : <Play size={28} className="ml-1" />}</button>
            <button onClick={onNext} data-testid="sl-np-next" className="text-white/90"><SkipForward size={28} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MusicTab({ onPlay, nowPlaying, playing }) {
  const [tracks, setTracks] = useState(null);
  useEffect(() => { api.get("/music/tracks?sort=trending").then(({ data }) => setTracks(data)); }, []);
  if (!tracks) return <Loading />;
  if (!tracks.length) return <Empty icon={Music2} title="No releases yet" hint="New music from VirtuStars creators will appear here." />;
  return (
    <div className="p-3 space-y-2">
      {tracks.map((t) => { const active = nowPlaying?.id === t.id; return (
        <button key={t.id} onClick={() => onPlay(t, tracks)} className="w-full surface rounded-xl p-2.5 flex items-center gap-3 text-left" data-testid={`sl-track-${t.id}`}>
          <div className="relative"><img src={t.cover_url} className="w-12 h-12 rounded object-cover" alt="" /><span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">{active && playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}</span></div>
          <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{t.title}</div><div className="text-[11px] text-muted-foreground truncate">{t.artist?.display_name} · {formatNum(t.stream_count)} streams</div></div>
        </button>
      ); })}
    </div>
  );
}

function MessagesTab({ user, onSignin, initialConv, onConvUsed }) {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(initialConv || null);
  useEffect(() => { if (initialConv) onConvUsed?.(); }, []); // eslint-disable-line
  const loadConvs = () => api.get("/conversations").then(({ data }) => setConvs(data)).catch(() => setConvs([]));
  useEffect(() => { if (user) loadConvs(); }, [user]); // eslint-disable-line

  if (!user) return <div className="p-6"><Empty icon={MessageSquare} title="Sign in to message" hint="Chat with creators you both follow." action={<button onClick={onSignin} className="btn-gold rounded-full px-4 py-2 text-sm">Sign in</button>} /></div>;
  if (active) return <PhoneThread user={user} conv={active} onBack={() => { setActive(null); loadConvs(); }} />;

  if (!convs) return <Loading />;
  if (!convs.length) return <Empty icon={MessageSquare} title="No conversations" hint="Message creators you both follow from the full site or their profile." />;
  return (
    <div className="p-3 space-y-2" data-testid="sl-conversations">
      {convs.map((c) => (
        <button key={c.id} onClick={() => setActive(c)} className="w-full surface rounded-xl p-2.5 flex items-center gap-3 text-left" data-testid={`sl-conv-${c.id}`}>
          <Avatar src={c.other?.avatar_url} name={c.other?.display_name} size={40} />
          <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{c.other?.display_name}</div><div className="text-[11px] text-muted-foreground truncate">{c.last_message || "Start chatting"}</div></div>
        </button>
      ))}
    </div>
  );
}

function TVTab() {
  const [vids, setVids] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/tv/videos").then(({ data }) => setVids(data)); }, []);
  if (!vids) return <Loading />;
  if (!vids.length) return <Empty icon={Tv} title="No shows yet" hint="VStar TV programming will appear here." />;
  return (
    <div className="p-3 space-y-3">
      {active && <video src={mediaUrl(active.video_url)} poster={active.thumbnail_url} controls autoPlay className="w-full rounded-xl aspect-video bg-black" data-testid="sl-tv-player" />}
      {vids.map((v) => (
        <button key={v.id} onClick={() => setActive(v)} className="w-full surface rounded-xl overflow-hidden text-left" data-testid={`sl-video-${v.id}`}>
          <div className="relative aspect-video bg-black"><img src={v.thumbnail_url} className="w-full h-full object-cover" alt="" loading="lazy" /><span className="absolute inset-0 flex items-center justify-center"><span className="w-10 h-10 rounded-full btn-gold flex items-center justify-center"><Play size={18} /></span></span></div>
          <div className="p-2.5"><div className="text-sm font-medium line-clamp-1">{v.title}</div><div className="text-[11px] text-muted-foreground">{v.channel?.display_name} · {formatNum(v.view_count)} views</div></div>
        </button>
      ))}
    </div>
  );
}

const NOTIF_ICON = { like: Heart, comment: MessageCircle, repost: Repeat2, follow: UserPlus, tip: Coins, message: MessageCircle, live: Radio, achievement: Trophy, milestone: Trophy };
const NOTIF_COLOR = { like: "text-rose-400", tip: "text-gold", follow: "text-eblue", live: "text-destructive", achievement: "text-gold", milestone: "text-gold" };

function NotificationsTab({ user, onSignin, onRead }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.post("/notifications/read").then(() => onRead?.()).catch(() => {});
  }, [user]); // eslint-disable-line
  if (!user) return <div className="p-6"><Empty icon={Bell} title="Sign in for notifications" action={<button onClick={onSignin} className="btn-gold rounded-full px-4 py-2 text-sm">Sign in</button>} /></div>;
  if (!items) return <Loading />;
  if (!items.length) return <Empty icon={Bell} title="All caught up" hint="New followers, likes, tips and chart moves show up here." />;
  const timeAgo = (d) => { try { return formatDistanceToNowStrict(new Date(d)) + " ago"; } catch { return ""; } };
  return (
    <div className="p-3" data-testid="sl-notifications">
      <div className="surface rounded-2xl divide-y divide-white/5">
        {items.map((n) => { const Icon = NOTIF_ICON[n.type] || Bell; return (
          <div key={n.id} className={`flex items-center gap-3 p-3 ${!n.read ? "bg-eblue/5" : ""}`} data-testid={`sl-notif-${n.id}`}>
            {n.actor ? <Avatar src={n.actor.avatar_url} name={n.actor.display_name} size={38} /> : <div className="w-9 h-9 rounded-full surface-2 flex items-center justify-center"><Icon size={16} /></div>}
            <div className="flex-1 min-w-0"><p className="text-sm leading-snug">{n.text}</p><div className="text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</div></div>
            <Icon size={15} className={NOTIF_COLOR[n.type] || "text-muted-foreground"} />
          </div>
        ); })}
      </div>
    </div>
  );
}

function Events() {
  const [events, setEvents] = useState(null);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const slRe = /^(https?:\/\/)?(maps\.secondlife\.com|slurl\.com)\//i;
  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)); }, []);
  const copy = (u) => { navigator.clipboard?.writeText(u); toast.success("Link copied"); };
  if (!events) return <Loading />;
  if (!events.length) return <Empty icon={Calendar} title="No events yet" hint="VirtuStars events will appear here." />;
  return (
    <div className="p-3 space-y-2">
      <div className="text-[11px] text-muted-foreground mb-1">Times shown in your timezone: {tz}</div>
      {events.map((e) => { const valid = e.join_url && slRe.test(e.join_url); return (
        <div key={e.id} className="surface rounded-xl overflow-hidden" data-testid={`sl-event-${e.id}`}>
          {e.artwork_url && <img src={mediaUrl(e.artwork_url)} className="w-full h-28 object-cover" alt="" loading="lazy" />}
          <div className="p-3">
            <div className="font-display font-semibold">{e.title}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{(() => { try { return new Date(e.datetime).toLocaleString(); } catch { return e.datetime; } })()} · {e.world} · {e.venue}</div>
            <div className="text-[11px] text-muted-foreground">Host: {e.host?.display_name}</div>
            {e.join_url && (valid
              ? <a href={e.join_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 btn-blue rounded-full px-3 py-1.5 text-xs"><ExternalLink size={12} /> Teleport</a>
              : <button onClick={() => copy(e.join_url)} className="mt-2 inline-flex items-center gap-1 surface-2 rounded-full px-3 py-1.5 text-xs"><Copy size={12} /> Copy destination link</button>)}
          </div>
        </div>
      ); })}
    </div>
  );
}

function Career({ user, onSignin }) {
  const [releases, setReleases] = useState(null);
  const [bookings, setBookings] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get(`/music/artist/${user.id}`).then(({ data }) => setReleases(data.tracks || [])).catch(() => setReleases([]));
    api.get("/bookings").then(({ data }) => setBookings(data || [])).catch(() => setBookings([]));
  }, [user]);
  if (!user) return <div className="p-6"><Empty icon={User} title="Sign in to view your career" action={<button onClick={onSignin} className="btn-gold rounded-full px-4 py-2 text-sm">Sign in</button>} /></div>;
  return (
    <div className="p-3 space-y-3">
      <div className="surface rounded-xl p-4 text-center" data-testid="sl-career-profile">
        <Avatar src={user.avatar_url} name={user.display_name} size={72} className="mx-auto" />
        <div className="font-display font-bold text-lg mt-2"><CreatorName user={user} /></div>
        <div className="text-xs text-muted-foreground">@{user.username}</div>
        {user.bio && <p className="text-sm mt-2">{user.bio}</p>}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Stat label="Followers" v={formatNum(user.follower_count)} /><Stat label="VStar" v={Number(user.vstar_score || 0).toFixed(0)} /><Stat label="Plan" v={user.plan === "plus" ? "Plus" : "Free"} />
        </div>
      </div>

      <div data-testid="sl-career-releases">
        <div className="flex items-center gap-2 px-1 mb-1.5"><Music2 size={15} className="text-gold" /><span className="text-sm font-semibold">My Releases</span></div>
        {releases === null ? <Loading /> : releases.length === 0 ? (
          <div className="surface rounded-xl p-4 text-center text-xs text-muted-foreground">No releases yet. Upload music on the full site.</div>
        ) : (
          <div className="space-y-2">{releases.map((t) => (
            <div key={t.id} className="surface rounded-xl p-2.5 flex items-center gap-3" data-testid={`sl-release-${t.id}`}>
              <img src={t.cover_url} className="w-11 h-11 rounded object-cover" alt="" loading="lazy" />
              <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{t.title}</div><div className="text-[11px] text-muted-foreground truncate">{formatNum(t.stream_count || 0)} streams{t.genre ? ` · ${t.genre}` : ""}</div></div>
            </div>
          ))}</div>
        )}
      </div>

      <div data-testid="sl-career-bookings">
        <div className="flex items-center gap-2 px-1 mb-1.5"><Calendar size={15} className="text-eblue" /><span className="text-sm font-semibold">My Bookings</span></div>
        {bookings === null ? <Loading /> : bookings.length === 0 ? (
          <div className="surface rounded-xl p-4 text-center text-xs text-muted-foreground">No bookings yet. Browse the Hire marketplace on the full site.</div>
        ) : (
          <div className="space-y-2">{bookings.map((b) => (
            <div key={b.id} className="surface rounded-xl p-3" data-testid={`sl-booking-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="text-sm font-medium truncate">{b.title}</div><div className="text-[11px] text-muted-foreground">{b.role === "provider" ? `Client: ${b.client?.display_name}` : `Provider: ${b.provider?.display_name}`}</div></div>
                <span className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full surface-2 text-muted-foreground">{b.status}</span>
              </div>
              <div className="text-[11px] text-gold mt-1">{formatNum(b.price_vc)} VC · {b.role === "provider" ? "Incoming" : "Outgoing"}</div>
            </div>
          ))}</div>
        )}
      </div>

      <a href="/home" target="_blank" rel="noreferrer" className="w-full surface-2 rounded-xl py-3 flex items-center justify-center gap-2 text-sm"><ExternalLink size={15} /> Open full VirtuStars website</a>
      <p className="text-[11px] text-muted-foreground text-center">Portfolio, uploads & advanced editing are available on the full site.</p>
    </div>
  );
}
function Stat({ label, v }) { return <div className="surface-2 rounded-lg p-2"><div className="font-display font-bold">{v}</div><div className="text-[10px] text-muted-foreground">{label}</div></div>; }

function More({ user, onTab, onSignin }) {
  const items = [["events", Calendar, "Events"], ["career", User, "My Career"]];
  return (
    <div className="p-3 space-y-2">
      {items.map(([k, Icon, label]) => <button key={k} onClick={() => onTab(k)} className="w-full surface rounded-xl p-4 flex items-center gap-3" data-testid={`sl-more-${k}`}><Icon size={20} className="text-eblue" /><span className="font-medium">{label}</span></button>)}
      <a href="/sl/setup" className="w-full surface rounded-xl p-4 flex items-center gap-3"><Smartphone size={20} className="text-gold" /><span className="font-medium">Phone Setup & Devices</span></a>
      {!user && <button onClick={onSignin} className="w-full btn-gold rounded-xl p-4 flex items-center justify-center gap-2"><LogIn size={18} /> Sign in to VirtuStars</button>}
    </div>
  );
}

// Vertical reels, matching the main app's Reels experience
function ReelsTab({ user, onSignin }) {
  const [reels, setReels] = useState(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => { api.get("/reels").then(({ data }) => setReels(data)).catch(() => setReels([])); }, []);
  if (!reels) return <div data-testid="sl-reels"><Loading /></div>;
  if (!reels.length) return <div data-testid="sl-reels"><Empty icon={Film} title="No reels yet" hint="VReels from creators will appear here." /></div>;
  return (
    <div className="relative h-full" data-testid="sl-reels">
      <button onClick={() => setMuted((m) => !m)} data-testid="sl-reel-mute" className="absolute top-3 right-3 z-30 w-9 h-9 rounded-full glass flex items-center justify-center">{muted ? <VolumeX size={17} /> : <Volume2 size={17} />}</button>
      <div className="h-full overflow-y-auto snap-y snap-mandatory hide-scroll">
        {reels.map((r) => <SLReel key={r.id} reel={r} user={user} onSignin={onSignin} muted={muted} />)}
      </div>
    </div>
  );
}

function SLReel({ reel, user, onSignin, muted }) {
  const [r, setR] = useState(reel);
  const [tipOpen, setTipOpen] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [cText, setCText] = useState("");
  const [cGif, setCGif] = useState(null);
  const [gifOpen, setGifOpen] = useState(false);
  const vref = useRef(); const wrap = useRef(); const viewed = useRef(false);

  useEffect(() => {
    const el = wrap.current; if (!el) return;
    const ob = new IntersectionObserver((e) => {
      const v = vref.current; if (!v) return;
      if (e[0].isIntersecting) { v.play().catch(() => {}); if (!viewed.current) { viewed.current = true; api.post(`/reels/${r.id}/view`).catch(() => {}); } }
      else v.pause();
    }, { threshold: 0.6 });
    ob.observe(el); return () => ob.disconnect();
  }, [r.id]);
  useEffect(() => { if (vref.current) vref.current.muted = muted; }, [muted]);

  const like = async () => { if (!user) return onSignin(); setR((x) => ({ ...x, liked: !x.liked, like_count: x.like_count + (x.liked ? -1 : 1) })); try { await api.post(`/reels/${r.id}/like`); } catch (e) { toast.error(errText(e)); } };
  const openComments = () => { setShowComments(true); if (comments === null) api.get(`/reels/${r.id}/comments`).then(({ data }) => setComments(data)).catch(() => setComments([])); };
  const addComment = async () => {
    if (!user) return onSignin();
    if (!cText.trim() && !cGif) return;
    try { const { data } = await api.post(`/reels/${r.id}/comment`, { text: cText, gif_url: cGif }); setComments((c) => [data, ...(c || [])]); setR((x) => ({ ...x, comment_count: (x.comment_count || 0) + 1 })); setCText(""); setCGif(null); }
    catch (e) { toast.error(errText(e)); }
  };
  const shareToStory = async () => {
    if (!user) return onSignin();
    try {
      await api.post("/stories", { media_url: r.video_url, media_type: "repost", text: r.caption || "",
        shared_ref: { kind: "reel", id: r.id, author: r.author, text: r.caption || "", media_url: r.video_url, media_type: "video" } });
      toast.success("Shared to your VirtuStars Story");
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div ref={wrap} className="relative h-full snap-start bg-black flex items-center" data-testid={`sl-reel-${r.id}`}>
      <video ref={vref} src={mediaUrl(r.video_url)} loop playsInline muted={muted} className="w-full max-h-full object-contain" onClick={() => { const v = vref.current; v.paused ? v.play() : v.pause(); }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />
      <div className="absolute left-3 bottom-5 right-16 z-10">
        <div className="flex items-center gap-2 mb-1.5"><Avatar src={r.author?.avatar_url} name={r.author?.display_name} size={32} ring /><div className="text-sm font-semibold"><CreatorName user={r.author} /></div></div>
        {r.caption && <p className="text-sm mb-1 line-clamp-3">{r.caption}</p>}
        {r.music && <div className="flex items-center gap-1.5 text-xs text-white/80"><Music2 size={12} /> {r.music}</div>}
      </div>
      <div className="absolute right-2 bottom-6 z-10 flex flex-col items-center gap-4">
        <SLAction icon={Heart} count={r.like_count} active={r.liked} onClick={like} testid={`sl-reel-like-${r.id}`} />
        <SLAction icon={MessageCircle} count={r.comment_count} onClick={openComments} testid={`sl-reel-comment-${r.id}`} />
        <SLAction icon={Repeat2} count={r.view_count} label="views" />
        <SLAction icon={Coins} label="tip" onClick={() => (user ? setTipOpen(true) : onSignin())} testid={`sl-reel-tip-${r.id}`} />
        {r.author?.username && <SLAction icon={PlusCircle} label="story" onClick={shareToStory} testid={`sl-reel-story-${r.id}`} />}
      </div>
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={r.author} refType="reel" refId={r.id} />

      {showComments && (
        <div className="absolute inset-0 z-20 flex flex-col justify-end" data-testid={`sl-reel-comments-${r.id}`}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowComments(false)} />
          <div className="relative bg-[#0a0a0f] border-t border-white/10 rounded-t-2xl max-h-[72%] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="font-display font-semibold text-sm">{formatNum(r.comment_count || 0)} comments</span>
              <button onClick={() => setShowComments(false)} data-testid={`sl-reel-comments-close-${r.id}`}><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {comments === null ? <Loading /> : comments.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Be the first to comment</p> : comments.map((c) => (
                <div key={c.id} className="flex gap-2.5">
                  <Avatar src={c.author?.avatar_url} name={c.author?.display_name} size={30} />
                  <div className="surface-2 rounded-2xl px-3 py-2 flex-1 min-w-0">
                    <div className="text-xs font-semibold">{c.author?.display_name}</div>
                    {c.text && <p className="text-sm whitespace-pre-wrap break-words">{c.text}</p>}
                    {c.gif_url && <img src={c.gif_url} alt="" className="mt-1.5 max-h-40 rounded-lg" loading="lazy" />}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/8 space-y-2">
              {cGif && <div className="relative w-fit"><img src={cGif} alt="" className="max-h-24 rounded-lg" /><button onClick={() => setCGif(null)} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center"><X size={13} /></button></div>}
              <div className="flex gap-2 items-center">
                <input value={cText} onChange={(e) => setCText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a comment…" className="flex-1 surface-2 border border-white/10 rounded-full px-4 h-10 text-sm outline-none" data-testid={`sl-reel-comment-input-${r.id}`} />
                <button onClick={() => setGifOpen(true)} data-testid={`sl-reel-comment-gif-${r.id}`} className="shrink-0 px-2.5 h-10 rounded-full surface-2 border border-white/10 text-[10px] font-bold tracking-wide text-eblue flex items-center">GIF</button>
                <button onClick={addComment} data-testid={`sl-reel-comment-send-${r.id}`} className="w-10 h-10 rounded-full btn-blue flex items-center justify-center shrink-0"><Send size={16} /></button>
              </div>
            </div>
          </div>
          <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onSelect={(url) => setCGif(url)} />
        </div>
      )}
    </div>
  );
}

function SLAction({ icon: Icon, count, active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1">
      <span className={`w-11 h-11 rounded-full glass flex items-center justify-center ${active ? "text-rose-400" : "text-white"}`}><Icon size={22} className={active ? "fill-rose-400" : ""} /></span>
      {(count != null || label) && <span className="text-[11px] font-mono">{count != null ? formatNum(count) : label}</span>}
    </button>
  );
}

// Profile screen mirroring the main mobile app profile
function ProfileTab({ user, onSignin, onTab }) {
  const [story, setStory] = useState(false);
  const [sub, setSub] = useState("dashboard");
  const [prof, setProf] = useState(null);
  const [posts, setPosts] = useState(null);
  const [reels, setReels] = useState(null);
  const [tracks, setTracks] = useState(null);
  const [people, setPeople] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/users/${user.username}`).then(({ data }) => setProf(data)).catch(() => setProf(user));
    api.get(`/users/${user.id}/posts`).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
    api.get("/reels").then(({ data }) => setReels(data.filter((r) => r.author_id === user.id))).catch(() => setReels([]));
    api.get(`/music/artist/${user.id}`).then(({ data }) => setTracks(data.tracks || [])).catch(() => setTracks([]));
  }, [user]);

  if (!user) return <div className="p-6"><Empty icon={User} title="Sign in to view your profile" action={<button onClick={onSignin} className="btn-gold rounded-full px-4 py-2 text-sm">Sign in</button>} /></div>;

  const u = prof || user;
  const cz = u.customization || {};
  const links = [["music", Music2, "VStar Music"], ["tv", Tv, "VStar TV"], ["messages", MessageSquare, "Messages"], ["crew", Shield, "My Vcrew"], ["events", Calendar, "Events"], ["career", User, "My Career"], ["settings", Settings, "Settings"]];
  const nameAnim = cz.animation?.config?.target === "name";

  return (
    <div className="pb-4" data-testid="sl-profile">
      <div className="relative h-24" style={bgStyle(cz.background) || { background: "linear-gradient(to bottom right,#131826,#000)" }} data-testid="sl-profile-bg">
        {u.banner_url && <img src={mediaUrl(u.banner_url)} className="w-full h-full object-cover" alt="" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] to-transparent" />
      </div>
      <div className="px-4 -mt-9 relative">
        <FramedAvatar src={u.avatar_url} name={u.display_name} size={76} frame={cz.frame} animation={cz.animation} ringClass="ring-4 ring-[#030303]" />
        <div className={`font-display font-bold text-lg mt-2 flex items-center gap-1.5 ${nameAnim ? "vs-anim-shimmer text-gold" : ""}`}><CreatorName user={u} /></div>
        <div className="text-xs text-muted-foreground">@{u.username}{u.roles?.primary ? ` · ${u.roles.primary}` : ""}</div>
        {u.bio && <p className="text-sm mt-2">{u.bio}</p>}

        {cz.badges?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="sl-profile-store-badges">
            {cz.badges.map((b) => <BadgePill key={b.id} badge={b} testid={`sl-profile-badge-${b.id}`} />)}
          </div>
        )}
        {u.virtucity_citizen && (
          <div className="mt-3" data-testid="sl-profile-virtucity-badge">
            <span className="text-xs rounded-full px-2.5 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1 font-semibold"><ShieldCheck size={12} />VirtuCity Citizen</span>
          </div>
        )}
        {(u.worlds?.length > 0 || u.roles?.secondary) && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {u.worlds?.map((w) => <span key={w} className="text-xs surface-2 rounded-full px-2.5 py-1">{w}</span>)}
            {[u.roles?.secondary].filter(Boolean).map((r) => <span key={r} className="text-xs rounded-full px-2.5 py-1 bg-eblue/10 text-eblue border border-eblue/30">{r}</span>)}
          </div>
        )}
        {u.award_wins?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="sl-profile-award-wins">
            {u.award_wins.slice().sort((a, b) => b.year - a.year).map((a, i) => (
              <span key={i} className="text-xs rounded-full px-2.5 py-1 bg-gold/15 text-gold border border-gold/40 flex items-center gap-1 font-medium" data-testid={`sl-award-win-${i}`}>
                <Trophy size={11} /> {a.year} {a.category} Winner
              </span>
            ))}
          </div>
        )}
        {u.achievements?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="sl-profile-achievements">
            {u.achievements.map((a, i) => <span key={i} className="flex items-center gap-1.5 text-xs glass glass-gold rounded-full px-3 py-1.5 text-gold"><Trophy size={13} />{a.label}</span>)}
          </div>
        )}

        <CitizenCard city={u.city} compact />

        <div className="grid grid-cols-3 gap-2 mt-3">
          <button onClick={() => setPeople("followers")} data-testid="sl-open-followers"><Stat label="Followers" v={formatNum(u.follower_count)} /></button>
          <button onClick={() => setPeople("following")} data-testid="sl-open-following"><Stat label="Following" v={formatNum(u.following_count || 0)} /></button>
          <Stat label="VStar" v={<VScore value={u.vstar_score} />} />
        </div>
        {people && <PeopleSheet userId={u.id} kind={people} onClose={() => setPeople(null)} />}
        <button onClick={() => setStory(true)} data-testid="sl-share-story-btn"
          className="w-full mt-3 group relative overflow-hidden rounded-xl p-[1.5px] bg-gradient-to-r from-eblue via-violet-500 to-gold">
          <span className="flex items-center justify-center gap-2 rounded-xl bg-[#0b0b14] px-4 py-3 font-semibold text-sm">
            <Instagram size={17} className="text-gold" /> Share my profile to Story
          </span>
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b border-white/10 mt-4 px-2 overflow-x-auto hide-scroll">
        {[["dashboard", "Dashboard"], ["posts", "Posts"], ["reels", "Reels"], ["music", "Music"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`sl-profile-sub-${k}`}
            className={`shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${sub === k ? "border-eblue text-eblue" : "border-transparent text-muted-foreground"}`}>
            {k === "dashboard" && <LayoutDashboard size={14} />}{l}
          </button>
        ))}
      </div>

      <div className="p-3">
        {sub === "dashboard" && <ProfileDashboard user={u} />}
        {sub === "posts" && (posts === null ? <Loading /> : posts.length === 0 ? <Empty icon={Grid3x3} title="No posts yet" /> : (
          <div className="grid grid-cols-3 gap-1" data-testid="sl-profile-posts">
            {posts.map((p) => p.media?.[0] ? (
              <img key={p.id} src={mediaUrl(p.media[0].url)} className="aspect-square object-cover rounded" alt="" loading="lazy" />
            ) : (
              <div key={p.id} className="aspect-square surface-2 rounded p-2 text-[10px] text-muted-foreground overflow-hidden">{p.text}</div>
            ))}
          </div>
        ))}
        {sub === "reels" && (reels === null ? <Loading /> : reels.length === 0 ? <Empty icon={Film} title="No reels yet" /> : (
          <div className="grid grid-cols-3 gap-1" data-testid="sl-profile-reels">
            {reels.map((r) => <video key={r.id} src={mediaUrl(r.video_url)} className="aspect-[9/16] object-cover rounded bg-black" muted />)}
          </div>
        ))}
        {sub === "music" && (tracks === null ? <Loading /> : tracks.length === 0 ? <Empty icon={Music2} title="No releases yet" /> : (
          <div className="space-y-2" data-testid="sl-profile-music">
            {tracks.map((t) => (
              <div key={t.id} className="surface rounded-xl p-2.5 flex items-center gap-3">
                <img src={t.cover_url} className="w-11 h-11 rounded object-cover" alt="" loading="lazy" />
                <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{t.title}</div><div className="text-[11px] text-muted-foreground truncate">{formatNum(t.stream_count || 0)} streams</div></div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="px-3 grid grid-cols-2 gap-2">
        {links.map(([k, Icon, label]) => <button key={k} onClick={() => onTab(k)} className="surface rounded-xl p-3 flex items-center gap-2 text-sm" data-testid={`sl-link-${k}`}><Icon size={17} className="text-eblue" />{label}</button>)}
        <a href="/sl/setup" className="surface rounded-xl p-3 flex items-center gap-2 text-sm"><Smartphone size={17} className="text-gold" />Phone Setup</a>
        <a href="/home" target="_blank" rel="noreferrer" className="surface rounded-xl p-3 flex items-center gap-2 text-sm"><ExternalLink size={17} />Full site</a>
      </div>

      {story && <StoryShareModal username={user.username} options={[{ type: "profile", label: "Profile" }]} qr onClose={() => setStory(false)} />}
    </div>
  );
}

// Simple create sheet for the SL phone (post with photos, or a story)
function CreateSheet({ user, defaultMode = "post", onClose }) {
  const [mode, setMode] = useState(defaultMode);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, 10 - images.length)) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((p) => [...p, data.url]);
      }
    } catch (e) { toast.error(errText(e)); } finally { setUploading(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "post") {
        const media = images.map((u) => ({ url: u, type: "image" }));
        if (!text && !media.length) { toast.error("Add something to post"); setBusy(false); return; }
        await api.post("/posts", { type: media.length ? "image" : "text", text, media });
        toast.success("Posted to VFeed");
      } else {
        if (!images.length) { toast.error("Pick a story image"); setBusy(false); return; }
        await api.post("/stories", { media_url: images[0], media_type: "image", text });
        toast.success("Story added");
      }
      onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-50 bg-black/70 flex items-end" onClick={onClose}>
      <div className="w-full glass rounded-t-2xl p-4 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="sl-create-sheet">
        <div className="flex items-center justify-between mb-3"><div className="font-display font-bold">Create</div><button onClick={onClose}><X size={20} /></button></div>
        <div className="flex gap-2 mb-3">
          {[["post", "Post"], ["story", "Story"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} data-testid={`sl-create-${k}`} className={`px-4 h-9 rounded-full text-sm font-medium ${mode === k ? "btn-blue text-white" : "surface-2 text-muted-foreground"}`}>{l}</button>)}
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={mode === "post" ? "Share with the industry…" : "Caption (optional)"} className="w-full surface-2 border border-white/10 rounded-xl p-3 text-sm outline-none min-h-[80px] mb-2" data-testid="sl-create-text" />
        <input ref={ref} type="file" accept="image/*" multiple={mode === "post"} className="hidden" onChange={(e) => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = ""; }} data-testid="sl-create-file" />
        {images.length > 0 && <div className="flex gap-2 overflow-x-auto no-scrollbar mb-2">{images.map((u, i) => <div key={i} className="relative shrink-0"><img src={mediaUrl(u)} className="w-20 h-20 rounded-lg object-cover" alt="" /><button onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center"><X size={12} /></button></div>)}</div>}
        <button onClick={() => ref.current?.click()} disabled={uploading || images.length >= 10} className="w-full surface-2 border border-dashed border-white/15 rounded-xl py-4 flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2" data-testid="sl-create-add-photo">
          {uploading ? <Loader2 size={18} className="animate-spin text-eblue" /> : <ImagePlus size={18} />} {uploading ? "Uploading…" : mode === "post" ? (images.length ? "Add more photos" : "Add photos") : "Add story image"}
        </button>
        <button onClick={submit} disabled={busy} className="btn-blue w-full rounded-xl h-11 text-sm" data-testid="sl-create-submit">{busy ? "Publishing…" : "Publish"}</button>
      </div>
    </div>
  );
}


function SignIn({ user, onClose, login, refresh }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const [pair, setPair] = useState(null);
  const pollRef = useRef();

  const doLogin = async (e) => { e.preventDefault(); setBusy(true); try { await login(email, pw); toast.success("Signed in"); onClose(); } catch (er) { toast.error(errText(er)); } finally { setBusy(false); } };

  const startPair = async () => {
    try {
      const { data } = await api.post("/sl/v1/pair/start", { device_name: "Second Life Phone" });
      setPair(data); setMode("pair");
      const check = async () => {
        try {
          const { data: s } = await api.get(`/sl/v1/pair/status?poll_secret=${encodeURIComponent(data.poll_secret)}`);
          if (s.status === "approved" && s.session_token) {
            clearInterval(pollRef.current);
            setToken(s.session_token);
            await refresh();
            toast.success("Device connected!");
            onClose();
          } else if (s.status === "expired" || s.status === "denied") {
            clearInterval(pollRef.current);
            toast.error(`Pairing ${s.status}`);
            setPair(null); setMode("login");
          }
        } catch (err) { /* transient network error — keep polling */ }
      };
      pollRef.current = setInterval(check, 2500);
      check();
    } catch (e) { toast.error(errText(e)); }
  };
  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <div className="absolute inset-0 z-50 bg-black/70 flex items-end" onClick={onClose}>
      <div className="w-full glass rounded-t-2xl p-4 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="sl-signin-sheet">
        <div className="flex items-center justify-between mb-3"><div className="font-display font-bold">{user ? "Account" : "Sign in to VirtuStars"}</div><button onClick={onClose}><X size={20} /></button></div>
        {user ? (
          <div className="flex items-center gap-3"><Avatar src={user.avatar_url} name={user.display_name} size={44} /><div><div className="font-semibold">{user.display_name}</div><div className="text-xs text-muted-foreground">@{user.username} · Connected device</div></div></div>
        ) : mode === "login" ? (
          <form onSubmit={doLogin} className="space-y-2">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className="w-full surface-2 border border-white/10 rounded-lg px-3 h-11 text-sm outline-none" data-testid="sl-login-email" />
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" required className="w-full surface-2 border border-white/10 rounded-lg px-3 h-11 text-sm outline-none" data-testid="sl-login-password" />
            <button disabled={busy} className="btn-gold w-full rounded-lg h-11 text-sm" data-testid="sl-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
            <button type="button" onClick={startPair} className="w-full surface-2 rounded-lg h-11 text-sm flex items-center justify-center gap-2" data-testid="sl-pair-start"><Link2 size={15} /> Connect via pairing code</button>
            <p className="text-[11px] text-muted-foreground text-center">Pairing lets you approve this device from your normal browser.</p>
          </form>
        ) : (
          <div className="text-center py-2">
            <div className="text-xs text-muted-foreground">Your pairing code</div>
            <div className="font-mono text-4xl tracking-[0.3em] text-gold my-3" data-testid="sl-pair-code">{pair?.code}</div>
            <p className="text-sm text-muted-foreground">Sign in at VirtuStars in your browser, open <b>/sl/setup</b>, and approve this code. Waiting for approval…</p>
            <Loading label="Waiting for approval" />
          </div>
        )}
      </div>
    </div>
  );
}
